import PadletApi from 'surface/padlet_api'
import uniqueId from 'lodash.uniqueid'
import debounce from 'lodash.debounce'
import wrap from 'lodash.wrap'
import memoize from 'lodash.memoize'
import { Commit, Dispatch } from 'vuex'
import { captureMessage } from 'bits/error_tracker'
import { FetchError } from 'surface/api_fetch'
import { Id, Cid, PostAttributes, Post } from 'types'

interface CommitAndDispatch {
  commit: Commit
  dispatch: Dispatch
}

interface CopyXferPostParams {
  wallId?: Id
  wallDescription?: string
  wallTitle?: string
  wallSectionId?: Id
  isDestinationThisWall?: boolean
  postPosition?: { left: number, top: number  }
  postId: Id,
  transferredPostCid?: Cid,
}

/**
 * Create post using PadletApi abstraction
 */
async function createPost ({ commit, dispatch }: CommitAndDispatch, postToBeSaved: PostAttributes): Promise<void> {
  try {
    commit('CREATED_POST', postToBeSaved)
    const obj = await PadletApi.Wish.create({
      ...postToBeSaved,
    })
    commit('CREATED_POST_SUCCESS', { ...obj, cid: postToBeSaved.cid })
    dispatch('syncPostBeingEditedCidForMap', { ...obj, cid: postToBeSaved.cid })
  } catch (e) {
    if (e.status === 403) {
      dispatch('showReloadNotification', null, { root: true })
    } else {
      commit('CREATED_POST_ERROR', postToBeSaved)
      const context = {
        response: e.message,
        attachment: postToBeSaved.attachment,
      }
      captureMessage(`CreatedPostError ${e.status || ''}`, { context })
    }
  }
}

/**
 * Delete post using PadletApi abstraction
 */
async function deletePost ({ commit, dispatch }: CommitAndDispatch, postToBeSaved: Post): Promise<boolean> {
  try {
    commit('DELETED_POST', postToBeSaved)
    const obj = await PadletApi.Wish.delete({
      id: postToBeSaved.id,
    })
    commit('DELETED_POST_SUCCESS', { ...obj, cid: postToBeSaved.cid })
    return true
  } catch (e) {
    if (e.status === 404) {
      dispatch('refresh', null, { root: true })
      return true
    }
    if (e.status === 403) {
      dispatch('showReloadNotification', null, { root: true })
      return false
    }
    commit('DELETED_POST_ERROR', postToBeSaved)
    const context = {
      response: e.message,
      postId: postToBeSaved.id,
    }
    captureMessage(`DeletedPostError ${e.status || ''}`, { context })
    return false
  }
}

/**
 * Delete all posts using PadletApi abstraction
 */
function deleteAllPosts ({ commit }: {commit: Commit}, posts: Post[]): void {
  posts.forEach((post): void => {
    PadletApi.Wish.delete({
      id: post.id,
    }).then((obj: Post): void => {
      commit('DELETED_POST', post)
      commit('DELETED_POST_SUCCESS', { ...obj, cid: post.cid })
    }).catch((e: FetchError): void => {
      commit('DELETED_POST_ERROR', post)
      const context = {
        response: e.message,
        action: 'deleteAllPosts',
      }
      captureMessage(`DeletedPostError ${e.status || ''}`, { context })
    })
  })
}

/**
 * Update post using PadletApi abstraction
 */
async function updatePost ({ commit, dispatch }: CommitAndDispatch, postToBeSaved: Post, retryIntervalRange: [number, number] = [20, 50]): Promise<void> {
  try {
    commit('UPDATED_POST', postToBeSaved)
    const obj: Post = await PadletApi.Wish.update({
      id: postToBeSaved.id,
      ...postToBeSaved,
    }, {
      minSleepTime: retryIntervalRange[0],
      maxSleepTime: retryIntervalRange[1],
    })
    commit('UPDATED_POST_SUCCESS', { ...obj, cid: postToBeSaved.cid })
  } catch (e) {
    if (e.status === 404) {
      dispatch('refresh', null, { root: true })
    } else if (e.status === 403) {
      dispatch('showReloadNotification', null, { root: true })
    } else {
      commit('UPDATED_POST_ERROR', postToBeSaved)
      const context = {
        response: e.message,
        postId: postToBeSaved.id,
      }
      captureMessage(`UpdatedPostError ${e.status || ''}`, { context })
    }
  }
}

async function copyPost ({ dispatch }, params: CopyXferPostParams): Promise<void> {
  const copyFunc = PadletApi.Wish.copy.bind(PadletApi.Wish)
  const { wallId, wallDescription, wallTitle, wallSectionId, postPosition } = params
  try {
    const postAttributes = await copyFunc({
      wishId: params.postId,
      wallId,
      wallDescription,
      wallTitle,
      wallSectionId,
      postPosition,
    })
    const isDestinationNewWall = !!wallTitle || !!wallDescription
    dispatch('postAction/updatePostCopied', {
      newPostData: postAttributes,
      originalPostId: params.postId,
      isDestinationNewWall,
    }, { root: true })
  } catch (error) {
    // contents of error
    // {
    //    "response": { /* response object */ },
    //    "status": 422, /* some 4xx error */
    //    "message": "{ \"error\": \"some error message\" }"
    // }
    // check to see if "message" can be JSON decoded
    let parsedError = error
    try {
      parsedError = JSON.parse(error.message)
    } finally {
      dispatch('postAction/updatePostCopied', { error: parsedError }, { root: true })
    }
  }
}

async function transferPost ({ dispatch }, params: CopyXferPostParams): Promise<void> {
  const { wallId, wallDescription, wallTitle, wallSectionId, isDestinationThisWall, transferredPostCid } = params
  const transferFunc = PadletApi.Wish.transfer.bind(PadletApi.Wish)
  try {
    // can transfer intra-wall, between shelf columns
    const postAttributes = await transferFunc({
      wishId: params.postId,
      wallId,
      wallDescription,
      wallTitle,
      wallSectionId,
      // postPosition,
    })
    const isDestinationNewWall = !!wallTitle || !!wallDescription
    dispatch('postAction/updatePostTransferred', {
      newPostData: postAttributes,
      originalPostId: params.postId,
      isDestinationThisWall,
      isDestinationNewWall,
      transferredPostCid,
    }, { root: true })
  } catch (error) {
    // contents of error
    // {
    //    "response": { /* response object */ },
    //    "status": 422, /* some 4xx error */
    //    "message": "{ \"error\": \"some error message\" }"
    // }
    // check to see if "message" can be JSON decoded
    let parsedError = error
    try {
      parsedError = JSON.parse(error.message)
    } finally {
      dispatch('postAction/updatePostTransferred', { error: parsedError }, { root: true })
    }
  }
}

/**
 * Get the next temp client ID for the next new post being added to
 * client-side list of posts.
 */
function nextTempClientId (): string {
  return uniqueId('c_new')
}

function generateClientId (post: PostAttributes): string {
  if (post.id) {
    return 'c' + post.id
  }
  return nextTempClientId()
}

function getClientId (post: Post): string|null {
  if (post.cid) {
    return post.cid
  }
  if (post.id) {
    return 'c' + post.id
  }
  return null
}
function isValidClientId (cid: Cid): boolean {
  if (!String(cid).startsWith('c')) {
    return false
  }
  return true
}

function isNew (post: Post|PostAttributes): boolean {
  return !post.id
}

function isEmpty (post: PostAttributes): boolean {
  return !post.subject && !post.body && !post.attachment
}

function isNonEmpty (post: PostAttributes): boolean {
  return !(isEmpty(post))
}

function savePost ({ commit, dispatch }: CommitAndDispatch, post: PostAttributes|Post): void {
  if (isEmpty(post)) {
    // do nothing
  } else if (isNew(post)) {
    createPost({ commit, dispatch }, post as PostAttributes)
  } else {
    updatePost({ commit, dispatch }, post as Post)
  }
}

// short for savePostFunctionSignature
type SPFS = ({ commit, dispatch }: CommitAndDispatch, post: PostAttributes|Post) => void
type memoizedSPFS = (post: PostAttributes|Post) => SPFS

const makeSavePostDebounced = (): SPFS => {
  /**
  * The call to `memoize()` will create and cache the debounced **function** based on the resolver function `(c, p) => p.cid || p.id || uniqueId()`.
  * So now we have a way to get the debounced version of save() for each of the post.
  * The cache will look like this:
  * ```js
  * {
    *  c5425: () => debounce(savePost, 500),
    *  c5426: () => debounce(savePost, 500)
    * }
    * ```
    * Each of the debounce function has a different timer, etc, and therefore each of the post is debounced separately.
    * The result of memoize will be a function of this shape:
    * ```
    * (c, p) => value of the key with the corresponding post cid/id/random id from the cache (which is a function)'
    * ```
    * Calling the value function with arguments (c, p) will trigger the debounced function
    * `wrap()` is used to do the above-mentioned step to call the function that was from the memoized cache
    **/
  const getDebouncedSavePostFunction = (): SPFS => debounce(savePost, 500)
  const getPostIdFromSavePostFunctionParams = (p: PostAttributes): string => p.cid || String(p.id) || uniqueId()
  const debouncedSavePostFunctionInstanceMap =  memoize(getDebouncedSavePostFunction, getPostIdFromSavePostFunctionParams) as memoizedSPFS
  const retrieveAndCallCachedMethod = (
    fn: memoizedSPFS, ...args: (CommitAndDispatch | PostAttributes)[]
  ): void => {
    const commitAndDispatch = args[0] as CommitAndDispatch
    const post = args[1] as PostAttributes
    const debouncedSavedPostInstance = fn(post)
    return debouncedSavedPostInstance(commitAndDispatch, post)
  }
  return wrap(debouncedSavePostFunctionInstanceMap, retrieveAndCallCachedMethod)
}

/**
 * Debounce save function per post
 * @param context
 * @param context.commit
 * @param post
 */
const savePostDebounced = makeSavePostDebounced()

export {
  createPost,
  deletePost,
  deleteAllPosts,
  updatePost,
  copyPost,
  transferPost,
  savePost,
  savePostDebounced,
  generateClientId,
  getClientId,
  isValidClientId,
  isNew,
  isEmpty,
  isNonEmpty,
}
